// src/App.tsx
import type { ReactNode } from "react";
import { Admin, CustomRoutes, Notification, Resource } from "react-admin";
import { Navigate, Route, Routes } from "react-router-dom";

import { AnalyticsProvider } from "./analytics/AnalyticsProvider";
import { authProvider } from "./auth/authProvider";
import { ChangePasswordScreen } from "./auth/ChangePasswordScreen";
import { CustomerServiceOrganizationList } from "./components/CustomerServiceOrganizationList";
import { CustomLogin } from "./components/CustomLogin";
import { DeviceList } from "./components/DeviceList";
import DeviceShow from "./components/DeviceShow";
import { IntegrationSettings } from "./components/IntegrationSettings";
import { InvitationList } from "./components/InvitationList";
import { LearningAssignmentList } from "./components/learning/LearningAssignmentList";
import { LearningProgressList } from "./components/learning/LearningProgressList";
import { CourseDetail } from "./components/learning/player/CourseDetail";
import { CourseList } from "./components/learning/player/CourseList";
import { FinalQuiz } from "./components/learning/player/FinalQuiz";
import { LessonPlayer } from "./components/learning/player/LessonPlayer";
import LocationCreate from "./components/LocationCreate";
import LocationEdit from "./components/LocationEdit";
import LocationList from "./components/LocationList";
import LocationShow from "./components/LocationShow";
import { OrganizationList } from "./components/OrganizationList";
import { OrganizationShow } from "./components/OrganizationShow";
import PatientCreate from "./components/PatientCreate";
import PatientEdit from "./components/PatientEdit";
import PatientList from "./components/PatientList";
import PatientShow from "./components/PatientShow";
import { PractitionerCreate } from "./components/PractitionerCreate";
import PractitionerEdit from "./components/PractitionerEdit";
import { PractitionerList } from "./components/PractitionerList";
import PractitionerShow from "./components/PractitionerShow";
import { ProcedureShow } from "./components/ProcedureShow";
import { RequirePermission } from "./components/RequirePermission";
import {
  BILLWERK_CUSTOMER_MATCH_ROUTE,
  BILLWERK_PLAN_VARIANTS_ROUTE,
  CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE,
  FINANCE_NOTIFICATIONS_ROUTE,
  FINANCE_OVERVIEW_ROUTE,
  INTEGRATIONS_ROUTE,
  LOGISTICS_DHL_SETTINGS_ROUTE,
  LOGISTICS_INVENTORY_ITEM_ROUTE,
  LOGISTICS_INVENTORY_ROUTE,
  LOGISTICS_REPORTS_ROUTE,
  LOGISTICS_ROUTE,
  LOGISTICS_WORKSPACE_ONE_ROUTE,
  MARKETING_DELIVERY_ROUTE,
  ResourceName,
  SALESFORCE_DASHBOARD_ROUTE,
  SALESFORCE_INTEGRATION_ROUTE,
  SALESFORCE_RECORDS_ROUTE,
  SALESFORCE_REPORTS_ROUTE,
  TEAM_ROUTE,
} from "./constants/resources";
import { i18nProvider } from "./i18n/i18nProvider";
import { LearningLayout } from "./layout/LearningLayout";
import { MainLayout } from "./layout/MainLayout";
import { getAuthData } from "./lib/authStorage";
import { CRM_CALLS_ROUTE, CRM_DUPLICATES_ROUTE, CRM_RECORD_ROUTE, CRM_ROUTE } from "./lib/crm";
import {
  CUSTOMER_SERVICE_ORGANIZATION_ROUTE,
  CUSTOMER_SERVICE_ORGANIZATIONS_ROUTE,
} from "./lib/customerServiceOrganizationRoutes";
import { MARKETING_ROUTE, WEB_ORDERS_ROUTE } from "./lib/marketing";
import { SALESFORCE_REPORTS_DEFAULT_PATH } from "./lib/salesforceReport";
import { AccountSettingsPage } from "./pages/AccountSettingsPage";
import { AnalyticsContent } from "./pages/AnalyticsContent";
import { AnalyticsDashboard } from "./pages/AnalyticsDashboard";
import { AnalyticsDistributions } from "./pages/AnalyticsDistributions";
import { AnalyticsFeatures } from "./pages/AnalyticsFeatures";
import { BillwerkCustomerMatchPage } from "./pages/BillwerkCustomerMatchPage";
import { BillwerkPlanVariantsPage } from "./pages/BillwerkPlanVariantsPage";
import { CrmCallsPage } from "./pages/CrmCallsPage";
import { CrmDuplicatesPage } from "./pages/CrmDuplicatesPage";
import { CrmHomePage } from "./pages/CrmHomePage";
import { CrmListPage } from "./pages/CrmListPage";
import { CrmRecordPage } from "./pages/CrmRecordPage";
import { CustomerServiceOrganizationPage } from "./pages/CustomerServiceOrganizationPage";
import { FinanceNotificationsPage } from "./pages/FinanceNotificationsPage";
import { FinanceOverviewPage } from "./pages/FinanceOverviewPage";
import { CustomerServiceIntegrationsPage } from "./pages/integrations/CustomerServiceIntegrationsPage";
import { DhlSettingsPage } from "./pages/logistics/DhlSettingsPage";
import { LogisticsInventoryItemPage } from "./pages/LogisticsInventoryItemPage";
import { LogisticsInventoryPage } from "./pages/LogisticsInventoryPage";
import { LogisticsPage } from "./pages/LogisticsPage";
import { LogisticsWorkspaceOnePage } from "./pages/LogisticsWorkspaceOnePage";
import { MarketingDeliveryPage } from "./pages/MarketingDeliveryPage";
import { MarketingOverviewPage } from "./pages/MarketingOverviewPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { PortalHome } from "./pages/PortalHome";
import { SalesforceDashboardPage } from "./pages/SalesforceDashboardPage";
import { SalesforceIntegrationPage } from "./pages/SalesforceIntegrationPage";
import { SalesforceRecordsPage } from "./pages/SalesforceRecordsPage";
import { SalesforceReportDashboardPage } from "./pages/SalesforceReportDashboardPage";
import { SalesforceReportPage } from "./pages/SalesforceReportPage";
import { TeamPage } from "./pages/TeamPage";
import { WebOrdersPage } from "./pages/WebOrdersPage";
import dataProvider from "./providers/dataProvider";
import { appTheme } from "./theme/appTheme";

const AppLayout = ({ children }: { children: ReactNode }) => {
  const authSurface = getAuthData()?.surface;
  if (authSurface === "learning") return <Navigate to="/learning" replace />;
  if (authSurface !== "admin") return <>{children}</>;
  return <MainLayout>{children}</MainLayout>;
};

const LearningPortalRoutes = () => {
  return (
    <Routes>
      <Route index element={<CourseList />} />
      <Route path="courses/:courseId" element={<CourseDetail />} />
      <Route path="courses/:courseId/lessons/:lessonId" element={<LessonPlayer />} />
      <Route path="courses/:courseId/quiz" element={<FinalQuiz />} />
      <Route path="*" element={<Navigate to="/learning" replace />} />
    </Routes>
  );
};

const RESOURCES = {
  [ResourceName.Invitation]: (
    <Resource key={ResourceName.Invitation} name={ResourceName.Invitation} list={InvitationList} />
  ),
  [ResourceName.Organization]: (
    <Resource
      key={ResourceName.Organization}
      name={ResourceName.Organization}
      list={OrganizationList}
      show={OrganizationShow}
    />
  ),
  [ResourceName.Location]: (
    <Resource
      key={ResourceName.Location}
      name={ResourceName.Location}
      list={LocationList}
      show={LocationShow}
      create={LocationCreate}
      edit={LocationEdit}
    />
  ),
  [ResourceName.Practitioner]: (
    <Resource
      key={ResourceName.Practitioner}
      name={ResourceName.Practitioner}
      list={PractitionerList}
      show={PractitionerShow}
      create={PractitionerCreate}
      edit={PractitionerEdit}
    />
  ),
  [ResourceName.Patient]: (
    <Resource
      key={ResourceName.Patient}
      name={ResourceName.Patient}
      list={PatientList}
      show={PatientShow}
      create={PatientCreate}
      edit={PatientEdit}
    />
  ),
  [ResourceName.Device]: (
    <Resource key={ResourceName.Device} name={ResourceName.Device} list={DeviceList} show={DeviceShow} />
  ),
  [ResourceName.Procedure]: (
    <Resource key={ResourceName.Procedure} name={ResourceName.Procedure} show={ProcedureShow} />
  ),
};

const MyNotification = () => <Notification autoHideDuration={100000} />;

const App = () => (
  <Admin
    theme={appTheme}
    i18nProvider={i18nProvider}
    authProvider={authProvider}
    dataProvider={dataProvider}
    loginPage={CustomLogin}
    layout={AppLayout}
    dashboard={PortalHome}
    notification={MyNotification}
  >
    {permissions => Object.values(RESOURCES).filter(resource => permissions?.includes(resource.props.name))}
    <CustomRoutes>
      <Route path="/account" element={<AccountSettingsPage />} />
      <Route path="/change-password" element={<ChangePasswordScreen />} />
      <Route
        path="/integration-settings"
        element={
          <RequirePermission permission={ResourceName.Location}>
            <IntegrationSettings />
          </RequirePermission>
        }
      />
      <Route
        path={INTEGRATIONS_ROUTE}
        element={
          <RequirePermission permission={ResourceName.Integrations}>
            <CustomerServiceIntegrationsPage />
          </RequirePermission>
        }
      />
      <Route path={SALESFORCE_DASHBOARD_ROUTE} element={<SalesforceDashboardPage />} />
      <Route path={SALESFORCE_REPORTS_ROUTE} element={<Navigate to={SALESFORCE_REPORTS_DEFAULT_PATH} replace />} />
      <Route path={`${SALESFORCE_REPORTS_ROUTE}/:dashboard`} element={<SalesforceReportDashboardPage />} />
      <Route
        path={`${SALESFORCE_REPORTS_ROUTE}/report/:reportId`}
        element={
          <RequirePermission permission={ResourceName.SalesforceReports}>
            <SalesforceReportPage />
          </RequirePermission>
        }
      />
      <Route path={SALESFORCE_RECORDS_ROUTE} element={<SalesforceRecordsPage />} />
      <Route path={SALESFORCE_INTEGRATION_ROUTE} element={<SalesforceIntegrationPage />} />
      <Route path={MARKETING_DELIVERY_ROUTE} element={<MarketingDeliveryPage />} />
      <Route path={TEAM_ROUTE} element={<TeamPage />} />
      <Route path={FINANCE_NOTIFICATIONS_ROUTE} element={<FinanceNotificationsPage />} />
      <Route path={BILLWERK_CUSTOMER_MATCH_ROUTE} element={<BillwerkCustomerMatchPage />} />
      <Route path={BILLWERK_PLAN_VARIANTS_ROUTE} element={<BillwerkPlanVariantsPage />} />
      <Route path={FINANCE_OVERVIEW_ROUTE} element={<FinanceOverviewPage />} />
      <Route
        path={`${LOGISTICS_REPORTS_ROUTE}/:reportId`}
        element={
          <RequirePermission permission={ResourceName.LogisticsReports}>
            <SalesforceReportPage context="logistics" />
          </RequirePermission>
        }
      />
      <Route path={LOGISTICS_DHL_SETTINGS_ROUTE} element={<DhlSettingsPage />} />
      <Route path={LOGISTICS_ROUTE} element={<LogisticsPage />} />
      <Route path={LOGISTICS_INVENTORY_ROUTE} element={<LogisticsInventoryPage />} />
      <Route path={LOGISTICS_WORKSPACE_ONE_ROUTE} element={<LogisticsWorkspaceOnePage />} />
      <Route path={LOGISTICS_INVENTORY_ITEM_ROUTE} element={<LogisticsInventoryItemPage />} />
      <Route path={CRM_ROUTE} element={<CrmHomePage />} />
      <Route path={CRM_CALLS_ROUTE} element={<CrmCallsPage />} />
      <Route path={CRM_DUPLICATES_ROUTE} element={<CrmDuplicatesPage />} />
      <Route path={`${CRM_ROUTE}/:objectSlug`} element={<CrmListPage />} />
      <Route path={CRM_RECORD_ROUTE} element={<CrmRecordPage />} />
      <Route path={MARKETING_ROUTE} element={<MarketingOverviewPage />} />
      <Route path={WEB_ORDERS_ROUTE} element={<WebOrdersPage />} />
      <Route
        path={CUSTOMER_SERVICE_ORGANIZATIONS_ROUTE}
        element={
          <RequirePermission permission={CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE}>
            <CustomerServiceOrganizationList />
          </RequirePermission>
        }
      />
      <Route
        path={`${CUSTOMER_SERVICE_ORGANIZATION_ROUTE}/*`}
        element={
          <RequirePermission permission={CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE}>
            <CustomerServiceOrganizationPage />
          </RequirePermission>
        }
      />
      <Route
        path="/onboarding"
        element={
          <RequirePermission permission={ResourceName.Location}>
            <OnboardingPage />
          </RequirePermission>
        }
      />
      <Route
        path="/LearningAssignment"
        element={
          <RequirePermission permission={ResourceName.LearningAssignment}>
            <LearningAssignmentList />
          </RequirePermission>
        }
      />
      <Route
        path="/LearningProgress"
        element={
          <RequirePermission permission={ResourceName.LearningProgress}>
            <LearningProgressList />
          </RequirePermission>
        }
      />
      <Route
        path="/analytics"
        element={
          <RequirePermission permission={ResourceName.Analytics}>
            <AnalyticsProvider />
          </RequirePermission>
        }
      >
        <Route index element={<Navigate to="/analytics/trends" replace />} />
        <Route path="trends" element={<AnalyticsDashboard />} />
        <Route path="features" element={<AnalyticsFeatures />} />
        <Route path="distributions" element={<AnalyticsDistributions />} />
        <Route path="content" element={<AnalyticsContent />} />
      </Route>
    </CustomRoutes>
    <CustomRoutes noLayout>
      <Route
        path="/learning/*"
        element={
          <RequirePermission permission={ResourceName.Learning}>
            <LearningLayout>
              <LearningPortalRoutes />
            </LearningLayout>
          </RequirePermission>
        }
      />
    </CustomRoutes>
  </Admin>
);

export default App;
